import omit from 'lodash/omit';
import type { CategoryData } from 'service/category/model';
import {
	type BazarCategoryTreeCategory,
	offerCategoryDataToCategoryData,
	getOffersCountMap,
} from 'service/category/utils';
import { type FilterSignalData, localFilterToRemote } from 'signals/filters';
import { type SearchUnpackedOffersParams, searchUnpackedOffers } from './resource';
import { remoteFilterToUnpackedOffersFilter } from './utils';
import { DEFAULT_OFFERS_VALUE } from 'signals/offers';
import type { UnpackedOfferData } from './model';

export async function getUnpackedCategories(
	filter: FilterSignalData,
	categoriesMap: Record<number, BazarCategoryTreeCategory>,
) {
	const filterParams = localFilterToRemote(filter);
	const categorySearchParams: SearchUnpackedOffersParams = {
		...remoteFilterToUnpackedOffersFilter(filterParams),
		limit: '1', // todo: once zbozi starts recognizing 0 as 0 and not as unset, change it to 0
		offset: '0',
	};
	if (!categorySearchParams.category_id) {
		// category.id === 0 => all categories => the endpoint does not work with that, instead we need to omit the id
		delete categorySearchParams.category_id;
	} else {
		// When we filter by a category that is a leaf (no children), we want to show its siblings instead
		const currentCategory = categoriesMap[Number(filterParams.category_id)];
		if (currentCategory?.children.length === 0 && currentCategory.parent) {
			categorySearchParams.category_id = currentCategory.parent.id.toString();
		}
	}

	const unpackedOffers = await searchUnpackedOffers(categorySearchParams);

	return unpackedOffers.categories.map((category) => omit(categoriesMap[category.id], 'parent', 'children'));
}

export async function getEshopCategoriesCounts(
	availableCategories: BazarCategoryTreeCategory[],
	filter: Omit<SearchUnpackedOffersParams, 'limit' | 'sort'>,
	headers?: HeadersInit,
) {
	let categories: CategoryData[] = [];

	try {
		categories = (
			await searchUnpackedOffers(
				{
					...omit(filter, 'category_id'),
					include_leaf_categories: true,
					offset: '0',
					limit: '1', // todo: once zbozi starts recognizing 0 as 0 and not as unset, change it to 0
				},
				headers,
			)
		).leaf_categories.map(offerCategoryDataToCategoryData);
	} catch (error) {
		console.error('getEshopCategoriesCounts error', error);
	}

	return getOffersCountMap(availableCategories, categories);
}

export async function getUnpackedOtherOffers(offer: UnpackedOfferData, limit: number = 7, headers?: HeadersInit) {
	const { id: offerId, category } = offer;
	const categoryId = category?.id;
	if (!categoryId) return DEFAULT_OFFERS_VALUE;

	const offerParams = {
		category_id: String(categoryId),
		limit: String(limit + 1),
	};

	const otherOffers = await searchUnpackedOffers(offerParams, headers).catch(() => undefined);

	if (!otherOffers) return DEFAULT_OFFERS_VALUE;

	// - odstranuji z dalsich inzeratu aktualne zobrazeny inzerat
	const newOfferList = otherOffers.results.filter((offer) => String(offer.id) !== String(offerId));

	// - pokud v seznamu aktualne zobrazeny inzerat neni, tak odeberu posledni inzerat ze seznamu
	if (newOfferList.length > limit) {
		newOfferList.pop();
	}

	return { ...otherOffers, results: newOfferList };
}
